import { findSearchType, type SearchTypeOption } from '../../config/propertyTypeCatalog';
import {
  RANGE_FILTER_KEYS,
  type SearchRangeBounds,
  type SearchRangeKey,
} from '../../config/searchFilterFields';
import type { FilterState } from './types';

/**
 * The /search filter changes as pure functions, shared by the filter bar's pills
 * and the full panel (PropertyFilters), so a change behaves identically wherever
 * it is made. Each returns the PARTIAL to hand to page.tsx's handleFilterChange,
 * which also prunes the fields a new type or scope hides.
 */

/**
 * Rent presets are yearly riyals and sale presets are sale prices, so a price
 * range picked for one scope means little in another: clear it (as the hero does).
 */
export function listingTypeChange(listingType: FilterState['listingType']): Partial<FilterState> {
  return { listingType, minPrice: '', maxPrice: '' };
}

/**
 * Picking a category keeps a still-valid type and only clears one that does not
 * belong to it (same semantics as the home hero).
 */
export function categoryChange(filters: FilterState, category: FilterState['category']): Partial<FilterState> {
  const keepType =
    !category ||
    (!!filters.propertyType && findSearchType(filters.propertyType)?.searchCategory === category);
  return keepType ? { category } : { category, propertyType: '' };
}

/** Picking a type syncs the category to the type's own bucket instead of clearing it. */
export function typeChange(
  filters: FilterState,
  propertyType: string,
  option: SearchTypeOption | null,
): Partial<FilterState> {
  return { propertyType, category: option?.searchCategory ?? filters.category };
}

/** One range (bedrooms, bathrooms, price, area) as its flat min/max filter keys. */
export function rangeChange(key: SearchRangeKey, next: SearchRangeBounds): Partial<FilterState> {
  const keys = RANGE_FILTER_KEYS[key];
  return { [keys.min]: next.min, [keys.max]: next.max } as Partial<FilterState>;
}

/** A typed amount belongs to its period: switching monthly/yearly clears the price range. */
export function pricePeriodChange(pricePeriod: FilterState['pricePeriod']): Partial<FilterState> {
  return { pricePeriod, minPrice: '', maxPrice: '' };
}

const AMENITY_KEYS = ['petFriendly', 'parking', 'furnished', 'swimmingPool', 'gym', 'garden', 'elevator', 'security'] as const;

/**
 * How many filters differ from the defaults (للإيجار + سكني), counted by GROUP:
 * a price range counts once, not as min + max, and picking a type counts once
 * even though it also syncs the category. Drives the «كل الفلاتر» badge.
 */
export function countActiveFilters(filters: FilterState, defaults: FilterState): number {
  let n = 0;
  if (filters.listingType !== defaults.listingType) n++;
  if (filters.propertyType) n++;
  else if (filters.category !== defaults.category) n++;
  if (filters.minPrice || filters.maxPrice) n++;
  if (filters.bedrooms || filters.bedroomsMax) n++;
  if (filters.bathrooms || filters.bathroomsMax) n++;
  if (filters.minArea || filters.maxArea) n++;
  if (filters.query.trim()) n++;
  if (filters.cityId || filters.areaId) n++;
  if (filters.landmark) n++;
  for (const key of AMENITY_KEYS) if (filters[key]) n++;
  return n;
}
